import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchAllGiver,
  fetchDetails,
} from '../../../../../../../lib/api/creditRequest';
import GlobalSearcher, {
  Filter,
} from '../../../../../../../lib/common/GlobalSearcher';
import CustomAlert from '../../../../../../../lib/designSystem/Alert';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../../lib/designSystem/HeaderWithActions';
import TableWithPagination from '../../../../../../../lib/designSystem/TableWithPagination';
import {
  hasAnyPermissions,
  validatePermissions,
} from '../../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../../services/redux/authSlice';
import CreateCreditLineDialog from './components/create';
import ReceivedDetails from './components/details';

export interface CreditRequest {
  status: string;
  companyReceiverId: number;
  productId: number;
  productName: string;
  description?: string;
}

const CreditsRequested = (props: any) => {
  const { businessIdentifierProp } = props;
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [workingCapitalRequests, setWorkingCapitalRequests] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [miniLoading, setMiniLoading] = useState(false);
  const [shoppingCartType, setShoppingCartType] = useState('');
  const [openShoppingCart, setOpenShoppingCart] = useState(false);
  const [openRequest, setOpenRequest] = useState<CreditRequest | null>(null);
  const [success, setSuccess] = useState(false);
  const [failed, setFailed] = useState(false);
  const [successText, setSuccessText] = useState('');
  const [failedMessage, setFailedText] = useState('');
  const [dataHeaders, setDataHeaders] = useState<any>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [status, setStatus] = useState('');
  const statusOptions = [
    { label: 'Todos', value: 'ALL' },
    { label: 'Completada', value: 'COMPLETED' },
    { label: 'Activa', value: 'ACTIVE' },
    { label: 'Aceptada', value: 'ACCEPTED' },
    { label: 'Rechazada', value: 'REJECTED' },
    { label: 'En Revisión', value: 'IN_REVIEW' },
    { label: 'En giro', value: 'IN_TRANSFER' },
  ];

  const dataHeadersDefault = [
    { key: 'id', label: 'ID', type: 'numeric-id' },
    { key: 'businessName', label: 'Cliente' },
    { key: 'type', label: 'Producto' },
    { key: 'amount', label: 'Monto', type: 'money' },
    { key: 'income', label: 'Ingreso', type: 'good-money' },
    { key: 'minutesSinceUpdate', label: 'Estancado', type: 'formatMinutes' },
    { key: 'operationDate', label: 'Fecha operación', type: 'date' },
    { key: 'createdAt', label: 'Creado en', type: 'date' },
    { key: 'status', label: 'Estado', type: 'status' },
    ...(validatePermissions(user, 'businesses_read_all')
      ? [{ key: 'ownerName', label: 'Ejecutivo' }]
      : []),
  ];

  const [selectedSortBy, setSelectedSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    const businessIdentifierQuery = businessIdentifierProp
      ? businessIdentifierProp
      : businessIdentifier;
    if (businessIdentifierQuery)
      query += `businessIdentifier=${businessIdentifierQuery}&`;
    if (status && status !== 'ALL') {
      query += `status=${status}&`;
    }
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    //if (validatePermissions(user, 'financing_factoring_read_all')) {
    //query += `executive=true&`;
    //}
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const fetchCreditRequests = async ({
    page = pagination.page,
    pageSize = pagination.pageSize,
  }: {
    page?: number;
    pageSize?: number;
  } = {}) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await fetchAllGiver(dispatch, query);
      setWorkingCapitalRequests(response.data);
      setPagination(response.pagination);
      setDataHeaders(dataHeadersDefault);
      setOpenShoppingCart(false);
    } catch (error) {
      alert('Error al cargar los pagos');
    }
    setLoading(false);
  };

  const triggerSearch = async () => {
    await fetchCreditRequests({ page: 0, pageSize: PAGE_SIZE });
  };

  const setSuccessOpen = (text: string) => {
    setSuccess(true);
    setSuccessText(text);
  };

  const setFailureOpen = (text: string) => {
    setFailed(true);
    setFailedText(text);
  };
  const handleDetails = async (id: number) => {
    setMiniLoading(true);
    try {
      setOpenDialog(false);
      const request = await fetchDetails(dispatch, id);
      setOpenRequest(request);
      setMiniLoading(false);
      setShoppingCartType(request.status);
      setOpenShoppingCart(true);
    } catch (error) {
      setLoading(false);
      return alert('error');
    }
  };

  useEffect(() => {
    fetchCreditRequests({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  const filters: Filter[] = [
    {
      type: 'date',
      label: 'Fecha de creación',
      value: startDate,
      dateKey: 'startDate',
      setter: setStartDate,
    },
    {
      type: 'date',
      label: 'Fecha de vencimiento',
      value: endDate,
      dateKey: 'endDate',
      setter: setEndDate,
    },
    {
      type: 'text',
      label: 'Rut cliente',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'select',
      label: 'Estado',
      value: status,
      setter: setStatus,
      options: statusOptions,
    },
    // ...(validatePermissions(user, 'financing_creditRequest_read_all')
    //   ? [
    //       {
    //         type: 'select' as const,
    //         label: 'Ejecutivo',
    //         value: executive,
    //         setter: setExecutive,
    //         options: executiveOptions,
    //       },
    //     ]
    //   : []),
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={() => triggerSearch()}
          selectedSortBy={selectedSortBy}
          setSelectedSortBy={setSelectedSortBy}
          // sortOptions={[
          //   { value: 'createdAt', label: 'Creación' },
          //   { value: 'dueDate', label: 'Vencimiento' },
          // ]}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
        />
      ),
    },
    {
      action: () => setOpenDialog(true),
      name: 'Solicitar',
      color: 'primary',
      disabled: !hasAnyPermissions(user, ['financing_credit_create']),
    },
  ];

  if (loading) return <CenteredCircularProgress />;

  return (
    <div>
      <HeaderWithActions
        title="Acá verás todas los créditos que se han solicitado"
        variant="body1"
        buttons={businessIdentifierProp ? [] : buttonsData}
      />
      {miniLoading && <CenteredCircularProgress type="layout" />}
      <TableWithPagination
        data={workingCapitalRequests}
        dataHeaders={dataHeaders}
        exportToFile="xlsx"
        pagination={pagination}
        rowsPerPageDefault={50}
        searchData={fetchCreditRequests}
        setPagination={setPagination}
        setData={setWorkingCapitalRequests}
        loading={loading}
        setLoading={setLoading}
        exportComplete={false}
        lastElement
        handleClickRow={handleDetails}
        finalSumRow
      />
      {openShoppingCart && (
        <ReceivedDetails
          shoppingCartType={shoppingCartType}
          openShoppingCart={openShoppingCart}
          setOpenShoppingCart={setOpenShoppingCart}
          openRequest={openRequest}
          setSelectedRequest={setOpenRequest}
          setUp={fetchCreditRequests}
          setSuccessOpen={setSuccessOpen}
          setFailureOpen={setFailureOpen}
        />
      )}
      {openDialog && (
        <CreateCreditLineDialog
          open={openDialog}
          setOpen={setOpenDialog}
          handleDetails={handleDetails}
          setUp={fetchCreditRequests}
          setSuccessOpen={setSuccessOpen}
          setFailureOpen={setFailureOpen}
          currentPage={pagination.page}
          currentPageSize={pagination.pageSize}
        />
      )}

      <CustomAlert
        setOpen={setSuccess}
        open={success}
        title="Exito"
        text={successText}
        severity="success"
      />
      <CustomAlert
        setOpen={setFailed}
        open={failed}
        title="Oops"
        text={failedMessage}
        severity="error"
      />
    </div>
  );
};

export default CreditsRequested;
